/**
 * ส่วน SSO ของแผงพนักงาน (D-153/D-155/D-157/D-158): อีเมล, app role รายแอป, รีเซ็ต MFA
 *
 * ทุกการเขียนโหลดสถานะจริงจากเซิร์ฟเวอร์ซ้ำ ไม่มี optimistic update; ปุ่มรีเซ็ต MFA ของตัวเอง
 * ปิดไว้ที่นี่เป็น UX เท่านั้น — เซิร์ฟเวอร์ปฏิเสธด้วย `422` อยู่แล้ว
 */

import { useEffect, useState } from 'react';

import { ConfirmModal } from './ConfirmModal';
import { RbacAdminFailureNotice } from './RbacAdminFailureNotice';
import { StatusBadge } from '../StatusBadge';
import {
  fetchAppRoleCatalog,
  fetchEmployeeAppRoles,
  resetEmployeeMfa,
  revokeEmployeeAppRole,
  setEmployeeAppRole,
  setEmployeeEmail,
  type RbacAdminFailure,
} from '../../lib/rbacAdmin/client';
import type {
  AppRoleCatalogEntry,
  Employee,
  EmployeeAppRole,
  MfaResetOutcome,
} from '../../lib/rbacAdmin/types';

export interface EmployeeSsoPanelProps {
  readonly employee: Employee;
  /** `employee_id` ของผู้ที่ล็อกอินอยู่ — ใช้ปิดปุ่มรีเซ็ต MFA ของตัวเอง */
  readonly selfEmployeeId: string | null;
  readonly canEdit: boolean;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** เรียกหลังตั้ง/ล้างอีเมลสำเร็จ เพื่อโหลดรายการพนักงานซ้ำ */
  readonly onEmployeeChanged: () => void;
}

type AppRolesState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly EmployeeAppRole[] }
  | RbacAdminFailure;

type CatalogState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly AppRoleCatalogEntry[] }
  | RbacAdminFailure;

type Pending = 'email' | 'set-role' | 'revoke' | 'reset-mfa';

export function EmployeeSsoPanel({
  employee,
  selfEmployeeId,
  canEdit,
  apiBaseUrl,
  fetchImpl,
  onEmployeeChanged,
}: EmployeeSsoPanelProps) {
  const employeeId = employee.employee_id;
  const isSelf = selfEmployeeId !== null && selfEmployeeId === employeeId;

  const [appRoles, setAppRoles] = useState<AppRolesState>({ status: 'loading' });
  const [catalog, setCatalog] = useState<CatalogState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);

  const [email, setEmail] = useState(employee.email ?? '');
  const [emailFailure, setEmailFailure] = useState<RbacAdminFailure | null>(null);
  const [emailSaved, setEmailSaved] = useState<'set' | 'cleared' | null>(null);

  const [roleApp, setRoleApp] = useState('');
  const [roleCode, setRoleCode] = useState('');
  const [roleFailure, setRoleFailure] = useState<RbacAdminFailure | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<EmployeeAppRole | null>(null);
  const [revokeFailure, setRevokeFailure] = useState<RbacAdminFailure | null>(null);

  const [confirmReset, setConfirmReset] = useState(false);
  const [resetFailure, setResetFailure] = useState<RbacAdminFailure | null>(null);
  const [resetDone, setResetDone] = useState<{ readonly outcome: MfaResetOutcome | null } | null>(
    null,
  );

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    setAppRoles((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));
    void fetchEmployeeAppRoles(employeeId, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setAppRoles(
        result.status === 'ready' ? { status: 'ready', items: result.roles.app_roles } : result,
      );
    });
    void fetchAppRoleCatalog(options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setCatalog(result.status === 'ready' ? { status: 'ready', items: result.page.items } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, employeeId, reloadToken]);

  const catalogItems = catalog.status === 'ready' ? catalog.items.filter((entry) => entry.active && entry.app_active) : [];
  const appCodes = Array.from(new Set(catalogItems.map((entry) => entry.app_code)));
  const rolesOfApp = catalogItems.filter((entry) => entry.app_code === roleApp);
  const options = { apiBaseUrl, fetchImpl };

  const submitEmail = async (next: string | null) => {
    if (pending !== null || !canEdit) {
      return;
    }
    setPending('email');
    setEmailFailure(null);
    setEmailSaved(null);
    const result = await setEmployeeEmail(employeeId, next, options);
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'done') {
      setEmailFailure(result);
      return;
    }
    setEmailSaved(next === null ? 'cleared' : 'set');
    if (next === null) {
      setEmail('');
    }
    onEmployeeChanged();
  };

  const submitRole = async () => {
    if (roleApp === '' || roleCode === '' || pending !== null || !canEdit) {
      return;
    }
    setPending('set-role');
    setRoleFailure(null);
    const result = await setEmployeeAppRole(employeeId, roleApp, { role_code: roleCode }, options);
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'done') {
      setRoleFailure(result);
      return;
    }
    setRoleCode('');
    setReloadToken((token) => token + 1);
  };

  const confirmRevoke = async () => {
    if (revokeTarget === null || pending !== null || !canEdit) {
      return;
    }
    setPending('revoke');
    setRevokeFailure(null);
    const result = await revokeEmployeeAppRole(employeeId, { app_code: revokeTarget.app_code }, options);
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'done') {
      setRevokeFailure(result);
      return;
    }
    setRevokeTarget(null);
    setReloadToken((token) => token + 1);
  };

  const confirmMfaReset = async () => {
    if (isSelf || pending !== null || !canEdit) {
      return;
    }
    setPending('reset-mfa');
    setResetFailure(null);
    setResetDone(null);
    const result = await resetEmployeeMfa(employeeId, options);
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'done') {
      // ปิด modal เพื่อให้เห็นเหตุผลที่ 422 บนหน้า (เช่น บัญชีกลุ่มอ่อนไหว)
      setConfirmReset(false);
      setResetFailure(result);
      return;
    }
    setConfirmReset(false);
    setResetDone({ outcome: result.outcome });
  };

  const items = appRoles.status === 'ready' ? appRoles.items : [];

  return (
    <div data-testid="employee-sso-panel">
      <h3 className="cap-card__title">SSO — อีเมลและสิทธิ์รายแอป</h3>

      {/* ----------------------------------------------------------- อีเมล -- */}

      <p className="cap-notice__text" data-testid="employee-email-current">
        อีเมลปัจจุบัน: <strong>{employee.email ?? 'ยังไม่ได้ตั้ง'}</strong>
      </p>

      {canEdit ? (
        <form
          className="cap-filters"
          data-testid="employee-email-form"
          aria-label="ตั้งหรือล้างอีเมลของพนักงานคนนี้"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = email.trim();
            if (trimmed !== '') {
              void submitEmail(trimmed);
            }
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">อีเมล</span>
            <input
              className="cap-input"
              type="email"
              name="email"
              data-testid="employee-email-input"
              value={email}
              autoComplete="off"
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </label>
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="employee-email-save"
            disabled={pending !== null || email.trim() === ''}
          >
            {pending === 'email' ? 'กำลังบันทึก…' : 'บันทึกอีเมล'}
          </button>
          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid="employee-email-clear"
            disabled={pending !== null || employee.email === null || employee.email === undefined}
            onClick={() => {
              void submitEmail(null);
            }}
          >
            ล้างอีเมล
          </button>
        </form>
      ) : null}

      {emailSaved === null ? null : (
        <p className="cap-notice__note" data-testid="employee-email-saved" role="status">
          {emailSaved === 'set' ? 'บันทึกอีเมลแล้ว' : 'ล้างอีเมลแล้ว'}
        </p>
      )}
      {emailFailure === null ? null : (
        <RbacAdminFailureNotice failure={emailFailure} context="set-email" employeeId={employeeId} />
      )}

      {/* ------------------------------------------------------- app role -- */}

      <h4 className="cap-card__title">App role</h4>

      {appRoles.status === 'loading' ? (
        <p className="cap-notice__text" data-testid="employee-app-roles-loading" role="status">
          กำลังโหลด app role…
        </p>
      ) : null}

      {appRoles.status !== 'loading' && appRoles.status !== 'ready' ? (
        <RbacAdminFailureNotice
          failure={appRoles}
          context="employee-app-roles"
          employeeId={employeeId}
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {appRoles.status === 'ready' && items.length === 0 ? (
        <p className="cap-notice__text" data-testid="employee-app-roles-empty">
          ยังไม่มี app role — พนักงานคนนี้เข้าแอปที่บังคับ app role ไม่ได้
        </p>
      ) : null}

      {items.length > 0 ? (
        <div className="cap-table-wrap">
          <table className="cap-table" data-testid="employee-app-roles-table">
            <thead>
              <tr>
                <th scope="col">แอป</th>
                <th scope="col">Role</th>
                <th scope="col">ให้โดย</th>
                <th scope="col">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {items.map((role) => (
                <tr key={role.app_code} data-testid={`employee-app-role-${role.app_code}`}>
                  <td>
                    <code className="cap-table__code">{role.app_code}</code>
                  </td>
                  <td>{role.role_code}</td>
                  <td>
                    {role.granted_by_employee_id ?? '—'}{' '}
                    {role.self_granted ? <StatusBadge tone="warning">ให้สิทธิ์ตัวเอง</StatusBadge> : null}
                  </td>
                  <td>
                    {canEdit ? (
                      <button
                        type="button"
                        className="cap-button cap-button--ghost"
                        data-testid={`employee-app-role-revoke-${role.app_code}`}
                        onClick={() => {
                          setRevokeFailure(null);
                          setRevokeTarget(role);
                        }}
                      >
                        ถอน
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {canEdit ? (
        <form
          className="cap-filters"
          data-testid="employee-app-role-form"
          aria-label="ตั้งหรือแทนที่ app role ของพนักงานคนนี้"
          onSubmit={(event) => {
            event.preventDefault();
            void submitRole();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">แอป</span>
            <select
              className="cap-input"
              data-testid="employee-app-role-app"
              value={roleApp}
              onChange={(event) => {
                setRoleApp(event.target.value);
                setRoleCode('');
              }}
            >
              <option value="">— เลือกแอป —</option>
              {appCodes.map((appCode) => (
                <option key={appCode} value={appCode}>
                  {appCode}
                </option>
              ))}
            </select>
          </label>
          <label className="cap-field">
            <span className="cap-field__label">Role</span>
            <select
              className="cap-input"
              data-testid="employee-app-role-role"
              value={roleCode}
              onChange={(event) => {
                setRoleCode(event.target.value);
              }}
            >
              <option value="">— เลือก role —</option>
              {rolesOfApp.map((entry) => (
                <option key={entry.role_code} value={entry.role_code}>
                  {entry.role_code}
                  {entry.description === '' ? '' : ` — ${entry.description}`}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="employee-app-role-save"
            disabled={pending !== null || roleApp === '' || roleCode === ''}
          >
            {pending === 'set-role' ? 'กำลังบันทึก…' : 'ตั้ง/แทนที่ role'}
          </button>
          <span className="cap-notice__note">หนึ่ง role ต่อแอปต่อพนักงาน — การตั้งใหม่แทนที่ role เดิม</span>
        </form>
      ) : null}

      {catalog.status !== 'loading' && catalog.status !== 'ready' ? (
        <RbacAdminFailureNotice failure={catalog} context="employee-app-roles" />
      ) : null}
      {roleFailure === null ? null : (
        <RbacAdminFailureNotice failure={roleFailure} context="set-app-role" employeeId={employeeId} />
      )}

      {/* ----------------------------------------------------------- MFA -- */}

      <h4 className="cap-card__title">MFA</h4>

      {canEdit ? (
        <>
          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid="employee-mfa-reset"
            disabled={isSelf || pending !== null}
            title={isSelf ? 'รีเซ็ต MFA ของตัวเองไม่ได้' : undefined}
            onClick={() => {
              setResetFailure(null);
              setResetDone(null);
              setConfirmReset(true);
            }}
          >
            รีเซ็ต MFA
          </button>
          {isSelf ? (
            <span className="cap-notice__note" data-testid="employee-mfa-reset-self">
              รีเซ็ต MFA ของบัญชีตัวเองไม่ได้ — ให้ผู้ดูแลอีกคนทำ
            </span>
          ) : null}
        </>
      ) : null}

      {resetDone === null ? null : (
        <p className="cap-notice__note" data-testid="employee-mfa-reset-done" role="status">
          รีเซ็ต MFA แล้ว — พนักงานต้องลงทะเบียน MFA ใหม่เมื่อล็อกอินครั้งถัดไป
          {resetDone.outcome === null
            ? ''
            : ` (เพิกถอน session ${resetDone.outcome.revoked_session_count} รายการ, session ของ IdP ${resetDone.outcome.revoked_idp_session_count} รายการ, grant ${resetDone.outcome.revoked_grant_count} รายการ)`}
        </p>
      )}
      {resetFailure === null ? null : (
        <RbacAdminFailureNotice failure={resetFailure} context="reset-mfa" employeeId={employeeId} />
      )}

      {/* ------------------------------------------------------ ยืนยัน -- */}

      <ConfirmModal
        open={revokeTarget !== null}
        title="ถอน app role"
        confirmLabel="ถอน role"
        busy={pending === 'revoke'}
        onConfirm={() => {
          void confirmRevoke();
        }}
        onCancel={() => {
          setRevokeTarget(null);
          setRevokeFailure(null);
        }}
      >
        <p className="cap-notice__text">
          ถอน role <strong>{revokeTarget?.role_code}</strong> ของแอป{' '}
          <code className="cap-table__code">{revokeTarget?.app_code}</code> จาก {employeeId} —
          refresh token ของแอปนี้ของเขาจะถูกเพิกถอนทันที
        </p>
        {revokeFailure === null ? null : (
          <RbacAdminFailureNotice failure={revokeFailure} context="revoke-app-role" employeeId={employeeId} />
        )}
      </ConfirmModal>

      <ConfirmModal
        open={confirmReset}
        title="รีเซ็ต MFA"
        confirmLabel="รีเซ็ต MFA"
        busy={pending === 'reset-mfa'}
        onConfirm={() => {
          void confirmMfaReset();
        }}
        onCancel={() => {
          setConfirmReset(false);
        }}
      >
        <p className="cap-notice__text" data-testid="employee-mfa-reset-warning">
          รีเซ็ต MFA ของ {employee.display_name} ({employeeId}): session และ refresh token ทั้งหมดของเขาจะถูก
          เพิกถอน และเขาต้องลงทะเบียน MFA ใหม่ตอนล็อกอินครั้งถัดไป
        </p>
      </ConfirmModal>
    </div>
  );
}
